import React, { useState, useEffect } from 'react';
import { Card, CardBody, Spinner, Progress } from '@nextui-org/react';
import { 
  FiUsers, 
  FiCheckCircle, 
  FiClock, 
  FiPause, 
  FiXCircle, 
  FiAlertTriangle,
  FiTrendingUp,
  FiUser,
  FiStar,
  FiBarChart2
} from 'react-icons/fi';
import { getProjectStatistics } from '../../../services/projects';

const AnalyticsComponent = ({ projectId }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // API'den istatistikleri yükle
  useEffect(() => {
    const fetchStatistics = async () => {
      if (!projectId) return;
      
      setLoading(true);
      setError('');
      try {
        const data = await getProjectStatistics(projectId);
        setStatistics(data);
      } catch (err) {
        setError('İstatistikler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [projectId]);

  // Öncelik seviyesi etiketleri
  const getPriorityLabel = (level) => {
    const labels = {
      1: 'Düşük',
      2: 'Orta', 
      3: 'Yüksek',
      4: 'Kritik',
      5: 'Acil'
    };
    return labels[level] || 'Bilinmeyen';
  };

  // Öncelik seviyesi renkleri
  const getPriorityColor = (level) => {
    const colors = {
      1: 'text-green-600 bg-green-100 dark:bg-green-900/20',
      2: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20',
      3: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20',
      4: 'text-orange-600 bg-orange-100 dark:bg-orange-900/20',
      5: 'text-red-600 bg-red-100 dark:bg-red-900/20'
    };
    return colors[level] || 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="text-gray-600 dark:text-gray-400 mt-4">İstatistikler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FiAlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Hata</h3>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FiBarChart2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Veri Bulunamadı</h3>
          <p className="text-gray-600 dark:text-gray-400">Bu proje için istatistik verisi bulunmuyor.</p>
        </div>
      </div>
    );
  }

  const { general_statistics, user_statistics, task_creators, priority_statistics } = statistics;

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Genel İstatistikler */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Genel İstatistikler</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Toplam Görevler */}
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Toplam Görevler</p>
                  <p className="text-3xl font-bold">{general_statistics.total_tasks}</p>
                </div>
                <FiBarChart2 className="w-8 h-8 text-blue-200" />
              </div>
            </CardBody>
          </Card>

          {/* Devam Eden Görevler */}
          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Devam Eden</p>
                  <p className="text-3xl font-bold">{general_statistics.ongoing_tasks}</p>
                </div>
                <FiClock className="w-8 h-8 text-yellow-200" />
              </div>
            </CardBody>
          </Card>

          {/* Tamamlanan Görevler */}
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Tamamlanan</p>
                  <p className="text-3xl font-bold">{general_statistics.completed_tasks}</p>
                </div>
                <FiCheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardBody>
          </Card>

          {/* Tamamlanma Oranı */}
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Tamamlanma Oranı</p>
                  <p className="text-3xl font-bold">{general_statistics.completion_rate}</p>
                </div>
                <FiTrendingUp className="w-8 h-8 text-purple-200" />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Detaylı Durum Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bekleyen</h3>
                <FiPause className="w-6 h-6 text-orange-500" />
              </div>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {general_statistics.pending_tasks}
              </p>
            </CardBody>
          </Card>

          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">İptal Edilen</h3>
                <FiXCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {general_statistics.cancelled_tasks}
              </p>
            </CardBody>
          </Card>

          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
            <CardBody className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Beklemede</h3>
                <FiPause className="w-6 h-6 text-gray-500" />
              </div>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {general_statistics.on_hold_tasks}
              </p>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Kullanıcı İstatistikleri */}
      {user_statistics && user_statistics.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Kullanıcı Performansı</h2>
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
            <CardBody className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Toplam
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tamamlanan
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Devam Eden
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Bekleyen
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tamamlanma Oranı
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {user_statistics.map((user, index) => (
                      <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.first_name} {user.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {user.total_assigned_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {user.completed_assigned_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                            {user.ongoing_assigned_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                            {user.pending_assigned_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1 mr-3">
                              <Progress 
                                value={parseFloat(user.completion_rate)} 
                                className="w-full"
                                color="success"
                                size="sm"
                              />
                            </div>
                            <span className="text-sm font-bold text-gray-900 dark:text-white min-w-[3rem]">
                              {user.completion_rate}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Görev Oluşturucu İstatistikleri */}
      {task_creators && task_creators.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Görev Oluşturucuları</h2>
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
            <CardBody className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Oluşturucu
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Toplam Oluşturulan
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tamamlanan
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Devam Eden
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tamamlanma Oranı
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {task_creators.map((creator, index) => (
                      <tr key={creator.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {creator.first_name.charAt(0)}{creator.last_name.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {creator.first_name} {creator.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {creator.created_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {creator.created_completed_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                            {creator.created_ongoing_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1 mr-3">
                              <Progress 
                                value={parseFloat(creator.completion_rate)} 
                                className="w-full"
                                color="primary"
                                size="sm"
                              />
                            </div>
                            <span className="text-sm font-bold text-gray-900 dark:text-white min-w-[3rem]">
                              {creator.completion_rate}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Öncelik İstatistikleri */}
      {priority_statistics && priority_statistics.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Öncelik Dağılımı</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {priority_statistics.map((priority, index) => (
              <Card key={priority.priority_level} className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-600 shadow-sm">
                <CardBody className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {getPriorityLabel(priority.priority_level)}
                    </h3>
                    <FiStar className="w-6 h-6 text-yellow-500" />
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Toplam Görev</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {priority.task_count}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Tamamlanan</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        {priority.completed_count}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Devam Eden</span>
                      <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                        {priority.ongoing_count}
                      </span>
                    </div>
                    
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tamamlanma Oranı</span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {priority.completion_rate}
                        </span>
                      </div>
                      <Progress 
                        value={parseFloat(priority.completion_rate)} 
                        className="w-full"
                        color="warning"
                        size="sm"
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsComponent;
